import { Clock, Sparkles } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Callout, pathFor,
  Diagram, MockSelect, MockField,
} from "../shared";

export default function ConceptsHandoffDeadlinesSection() {
  return (
    <div data-testid="guide-concepts-handoff-deadlines">
      <PageHeading
        icon={Clock}
        title="Handoff Deadlines & Fairness"
        description="A small but fair idea added in v2.10. When you pass an item to someone, you can optionally tell the system when you'd like it back and why — so the record shows what people meant, not just that the item moved."
      />

      <Callout variant="info" title="In plain words">
        When you hand work to someone, you often have a quiet expectation in your head — "I'd like this back by Friday". These two optional boxes let you write that expectation down so it's remembered. They don't change deadlines or send extra emails; they're just polite notes that stick to the record.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Fill these in whenever a handoff has a real expectation behind it. Leave them blank for routine pass-throughs — the handoff works exactly the same either way.
        </p>
      </Section>

      <Section title="The two optional boxes">
        <p className="text-sm text-white/65 leading-relaxed mb-3">
          They appear on every handoff path — the inline Hand-to dropdown on Updates, the Hand to control on the detail page, a member's self-handoff, and the override-handoff pop-up — just under the destination picker.
        </p>
        <Diagram
          title="Where the two boxes appear"
          frameLabel="Hand off — optional deadline"
          legend={[
            <>First pick who you're handing to, as usual.</>,
            <><span className="text-white">Due back by</span> — the date you'd like it back. (It does NOT change the official due date and sends no reminders.)</>,
            <><span className="text-white">Why this internal date?</span> — a short note explaining your date, which the receiver will see.</>,
          ]}
        >
          <MockSelect label="Hand to" value="Amal R." marker={1} />
          <MockField label="Due back by (optional)" value="Fri 14 Feb" marker={2} />
          <MockField label="Why this internal date? (optional)" placeholder="e.g. I need 2 days to review before the board pack closes" marker={3} />
        </Diagram>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div className="p-3 rounded-md bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2 mb-1.5">
              <Clock className="w-3.5 h-3.5 text-cyan-300" />
              <p className="text-sm text-white font-medium">Due back by</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              An <span className="text-white">internal deadline for the receiver</span> — the date you'd like the item back in your hands (or in the next stage's queue). It does <em>not</em> change the item's official due date and creates no emails or reminders. Think of it as a polite hint that becomes part of the record.
            </p>
          </div>
          <div className="p-3 rounded-md bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <p className="text-sm text-white font-medium">Why this internal date?</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              A short note explaining your date. For example: <em>"I need 2 days to review before the board pack closes",</em> <em>"Quick eyes-only check — anything by Friday is fine",</em> <em>"Blocked until you confirm the assumption."</em> The receiver sees this on their handoff email and on the item's Responsibility Timeline.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Why this exists">
        <p className="text-sm text-white/65 leading-relaxed">
          Judging people only by an item's official due date can be unfair to handoffs. If you receive an item on Tuesday but the official deadline is already Wednesday, no amount of speed will look "on time". Recording the receiver's real window — the date their predecessor actually expected — lets the record tell a fairer story than the original due date alone.
        </p>
        <Callout variant="info" title="For now, this just records — nothing else">
          In this first phase, the two boxes are <span className="text-white">saved</span> on the handoff, shown on the Responsibility Timeline and the handoff email, and that's it. They don't change the due date, they don't change any performance numbers, and they don't send their own reminders. Future releases will build on this captured data.
        </Callout>
      </Section>

      <Section title="When to use each box">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li><span className="text-white">Both filled.</span> Best for serious handoffs with a real expectation — the receiver knows what's expected and can push back if it's unrealistic.</li>
          <li><span className="text-white">Just the date.</span> Fine when the context is obvious ("end of day Friday, before the board meeting we both know about").</li>
          <li><span className="text-white">Just the note.</span> Fine when there's no hard date but you want to convey urgency ("urgent — please bump above your inbox").</li>
          <li><span className="text-white">Neither.</span> Perfectly fine. The handoff works exactly as before. Use this for routine pass-throughs.</li>
        </ul>
      </Section>

      <Section title="Where the recorded notes show up">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li><span className="text-white">The handoff email to the receiver</span> — the date and note appear right below the standard handoff details.</li>
          <li><span className="text-white">The Responsibility Timeline card</span> on the item's <Link href={pathFor("pages/detail")} className="text-cyan-300 hover:underline">detail page</Link> — each segment shows its internal deadline and explanation.</li>
          <li><span className="text-white">The item's history record</span> — stored on the handoff event so the information is kept safely even if the timeline display changes.</li>
        </ul>
      </Section>

      <Callout variant="tip" title="A note on fairness">
        An earlier fix (v2.7) already credits "met / missed deadline" counts to whoever was actually <em>holding</em> the item when it closed — not always to the original owner. This new capture builds on that: a future release will let you choose to judge a holder by the internal due date rather than the original due date. For now, just record honestly and the data will be ready.
      </Callout>
    </div>
  );
}
